// "Export my data" (CER-70): the package the database makes
// (private.export_data), read here and turned into the Markdown a person can
// open without Cerebro. Settings on the website and the connector function
// both use this file, so the readable copy is made one way. It imports
// nothing, so Deno can load it as it is.

/** A row as the database exported it: every column of its table. */
export type ExportRow = Record<string, unknown>;

/** Generated from private.export_data array keys. Run node scripts/generate/export-tables.mjs; do not edit the list. */
export const EXPORT_TABLES = [
  'areas',
  'captures',
  'checkin_answers',
  'checkins',
  'classifications',
  'custom_categories',
  'digests',
  'feedback',
  'feedback_completions',
  'feedback_confirmations',
  'ideas',
  'meeting_imports',
  'note_people',
  'notes',
  'outcomes',
  'people',
  'projects',
  'recurring_tasks',
  'resources',
  'task_people',
  'tasks',
] as const;
export type ExportTable = (typeof EXPORT_TABLES)[number];

export type ExportData = { format: 1; exported_at: string; profile: ExportRow | null } & Record<ExportTable, ExportRow[]>;

const isRecord = (value: unknown): value is ExportRow => typeof value === 'object' && value !== null && !Array.isArray(value);

/** The package as export_my_data() returns it; throws when it is not one, so a broken backup is never saved as a good one. */
export function parseExport(data: unknown): ExportData {
  const malformed = (what: string) => new Error(`the export did not arrive whole: ${what}`);
  if (!isRecord(data)) throw malformed('it is not a package');
  if (data.format !== 1) throw malformed('its format is not one this version reads');
  if (typeof data.exported_at !== 'string' || Number.isNaN(Date.parse(data.exported_at))) throw malformed('it has no time');
  if (!(data.profile === null || isRecord(data.profile))) throw malformed('profile');
  for (const table of EXPORT_TABLES) {
    const rows = data[table];
    if (!Array.isArray(rows) || !rows.every(isRecord)) throw malformed(table);
  }
  return data as ExportData;
}

/** The files' shared name: cerebro-diem-2026-10-04, from the package's own time (UTC). */
export const exportFileStem = (data: Pick<ExportData, 'exported_at'>): string =>
  `cerebro-diem-${new Date(data.exported_at).toISOString().slice(0, 10)}`;

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const day = (value: unknown): string => text(value).slice(0, 10);
/** A body under a list item or heading: its lines kept, indented so Markdown reads them as part of it. */
const indented = (body: string, by: string): string[] => body.split('\n').map((line) => `${by}${line}`.trimEnd());

function taskLine(task: ExportRow): string[] {
  const done = task.status === 'done';
  const facts = [
    done && day(task.completed_at) ? `done ${day(task.completed_at)}` : '',
    !done && text(task.status) && task.status !== 'pending' ? text(task.status) : '',
    !done && text(task.gtd_status) ? text(task.gtd_status) : '',
    text(task.context),
    day(task.due_date) ? `due ${day(task.due_date)}` : '',
    text(task.remind_at) ? `reminder ${text(task.remind_at)}` : '',
  ].filter((fact) => fact !== '');
  const line = `- [${done ? 'x' : ' '}] ${text(task.name)}${facts.length > 0 ? ` (${facts.join(', ')})` : ''}`;
  return text(task.notes) ? [line, ...indented(text(task.notes), '  ')] : [line];
}

function noteBlock(note: ExportRow): string[] {
  return [`#### ${text(note.title) || 'Untitled note'}`, '', ...(text(note.body) ? [...indented(text(note.body), ''), ''] : [])];
}

/**
 * The whole package as one Markdown document: each project, area and
 * resource with its tasks and notes beneath it, then what has no container,
 * then people, ideas and every capture as it was said. It is for reading and
 * searching; the JSON beside it is the complete copy.
 */
export function exportMarkdown(data: ExportData): string {
  const out: string[] = ['# Cerebro Diem export', '', `Exported ${data.exported_at}`, ''];
  const active = data.outcomes.filter((outcome) => !text(outcome.retired_at));
  if (active.length > 0) {
    out.push('## What matters most', '');
    active.forEach((outcome, index) => {
      out.push(`${index + 1}. ${text(outcome.title)}`);
      if (text(outcome.description)) out.push(...indented(text(outcome.description), '   '));
    });
    out.push('');
  }
  const placed = new Set<unknown>();
  const section = (heading: string, kind: 'project_id' | 'area_id' | 'resource_id', containers: ExportRow[]) => {
    if (containers.length === 0) return;
    out.push(`## ${heading}`, '');
    for (const container of containers) {
      const archived = text(container.archived_at) ? ` (archived ${day(container.archived_at)})` : '';
      out.push(`### ${text(container.name)}${archived}`, '');
      const about = text(container.description) || text(container.notes);
      if (about) out.push(...indented(about, ''), '');
      if (text(container.next_action)) out.push(`Next action: ${text(container.next_action)}`, '');
      const tasks = kind === 'resource_id' ? [] : data.tasks.filter((task) => task[kind] === container.id && !placed.has(task.id));
      for (const task of tasks) {
        placed.add(task.id);
        out.push(...taskLine(task));
      }
      if (tasks.length > 0) out.push('');
      for (const note of data.notes.filter((n) => n[kind] === container.id && !placed.has(n.id))) {
        placed.add(note.id);
        out.push(...noteBlock(note));
      }
    }
  };
  section('Projects', 'project_id', data.projects);
  section('Areas', 'area_id', data.areas);
  section('Resources', 'resource_id', data.resources);

  const looseTasks = data.tasks.filter((task) => !placed.has(task.id));
  if (looseTasks.length > 0) out.push('## Tasks in no project or area', '', ...looseTasks.flatMap(taskLine), '');
  const looseNotes = data.notes.filter((note) => !placed.has(note.id));
  if (looseNotes.length > 0) out.push('## Notes in no project, area or resource', '', ...looseNotes.flatMap(noteBlock));

  if (data.people.length > 0) {
    out.push('## People', '');
    for (const person of data.people) {
      out.push(`### ${text(person.name)}`, '');
      if (text(person.context)) out.push(...indented(text(person.context), ''), '');
      // Follow-ups are the open tasks linked to the person (CER-140); each is also listed where it lives.
      const followUps = data.task_people
        .filter((link) => link.person_id === person.id)
        .map((link) => data.tasks.find((task) => task.id === link.task_id))
        .filter((task): task is ExportRow => task !== undefined && task.status !== 'done');
      if (followUps.length > 0) {
        out.push(
          ...followUps.map((task) => `- Follow up: ${text(task.name)}${day(task.due_date) ? ` (due ${day(task.due_date)})` : ''}`),
          '',
        );
      }
    }
  }
  if (data.ideas.length > 0) {
    out.push('## Ideas', '');
    for (const idea of data.ideas) {
      out.push(`### ${text(idea.title)}`, '');
      for (const part of [text(idea.one_liner), text(idea.notes)]) if (part) out.push(...indented(part, ''), '');
    }
  }
  if (data.captures.length > 0) {
    out.push('## Captures', '');
    for (const capture of data.captures) {
      const said = text(capture.raw_text);
      const [first, ...rest] = said.split('\n');
      out.push(`- ${text(capture.created_at)} (${text(capture.source) || 'unknown'}): ${first}`, ...indented(rest.join('\n'), '  ').filter((l) => l !== ''));
    }
    out.push('');
  }
  return `${out.join('\n').trimEnd()}\n`;
}
